import { io } from 'socket.io-client'

class SocketService {
  socket = null
  reconnectAttempts = 0
  // Effectively unlimited: a backend deploy (new container/instance, health
  // checks, DB/Redis reconnection) can easily take well past the ~17s that
  // 5 attempts * exponential-backoff-capped-at-5s covers. socket.io-client
  // stops retrying entirely once reconnectionAttempts is exhausted -- it
  // does NOT try again later on its own -- so a low cap here meant every
  // open tab lost its socket for good the moment a deploy outlasted 17s,
  // with no error visible to the user beyond silence in the UI.
  maxReconnectAttempts = Infinity
  reconnectDelay = 1000
  reconnectTimer = null
  connectionCallbacks = []

  connect(token) {
    if (!this.socket) {
      this.socket = io(new URL(process.env.REACT_APP_END_POINT).origin, {
        transports: ['polling', 'websocket'],
        auth: { token },
        timeout: 20000,
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
        reconnectionDelayMax: 5000,
        autoConnect: true,
      })

      this.setupEventListeners()
    }
    return this.socket
  }

  setupEventListeners() {
    if (!this.socket) return

    this.socket.on('connect', () => {
      console.log('✅ Socket connected:', this.socket.id)
      this.reconnectAttempts = 0
      this.notifyConnectionCallbacks('connect', { socketId: this.socket.id })
    })

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason)
      this.notifyConnectionCallbacks('disconnect', { reason })

      if (reason === 'io server disconnect') {
        // Server disconnected, try to reconnect
        this.socket.connect()
      }
    })

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error)
      this.notifyConnectionCallbacks('connect_error', { error: error.message })
      // No manual retry here -- `reconnection: true` in connect() already
      // has socket.io-client's own Manager retrying on a loop (capped at
      // reconnectionDelayMax=5s between tries, unlimited attempts). A second,
      // independent retry loop here used to race against it and, once
      // maxReconnectAttempts stopped being a small bounded number, its delay
      // formula (reconnectDelay * 2^attempts) had no ceiling and would grow
      // unbounded during a long outage.
    })

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Socket reconnected after', attemptNumber, 'attempts')
      this.reconnectAttempts = 0
      this.notifyConnectionCallbacks('reconnect', { attemptNumber })
    })

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 Attempting to reconnect:', attemptNumber)
      this.notifyConnectionCallbacks('reconnect_attempt', { attemptNumber })
    })

    this.socket.on('reconnect_error', (error) => {
      console.error('❌ Socket reconnection error:', error)
      this.notifyConnectionCallbacks('reconnect_error', {
        error: error.message,
      })
    })

    this.socket.on('reconnect_failed', () => {
      console.error(
        '❌ Socket reconnection failed after',
        this.maxReconnectAttempts,
        'attempts',
      )
      this.notifyConnectionCallbacks('reconnect_failed', {
        maxAttempts: this.maxReconnectAttempts,
      })
    })
  }

  addConnectionCallback(callback) {
    this.connectionCallbacks.push(callback)
  }

  removeConnectionCallback(callback) {
    const index = this.connectionCallbacks.indexOf(callback)
    if (index > -1) {
      this.connectionCallbacks.splice(index, 1)
    }
  }

  notifyConnectionCallbacks(event, data) {
    this.connectionCallbacks.forEach((callback) => {
      try {
        callback(event, data)
      } catch (error) {
        console.error('Error in connection callback:', error)
      }
    })
  }

  getSocket() {
    return this.socket
  }

  isConnected() {
    return this.socket && this.socket.connected
  }

  getConnectionStatus() {
    if (!this.socket) return { connected: false, status: 'disconnected' }

    return {
      connected: this.socket.connected,
      status: this.socket.connected ? 'connected' : 'disconnected',
      socketId: this.socket.id,
      reconnectAttempts: this.reconnectAttempts,
    }
  }

  emit(event, data) {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, data)
      return true
    } else {
      console.warn('Socket not connected, cannot emit event:', event)
      return false
    }
  }

  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback)
    }
  }

  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback)
    }
  }

  // Enhanced emit with acknowledgment
  emitWithAck(event, data, timeout = 5000) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.socket.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      const timer = setTimeout(() => {
        reject(new Error('Socket emit timeout'))
      }, timeout)

      this.socket.emit(event, data, (response) => {
        clearTimeout(timer)
        if (response && response.success) {
          resolve(response)
        } else {
          reject(new Error(response?.error || 'Socket emit failed'))
        }
      })
    })
  }

  // Join a specific room/channel
  joinRoom(roomId, userId) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('join_room', { roomId, userId })
      return true
    }
    return false
  }

  // Leave a specific room/channel
  leaveRoom(roomId, userId) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('leave_room', { roomId, userId })
      return true
    }
    return false
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }

    this.connectionCallbacks = []
    this.reconnectAttempts = 0
  }

  // Force reconnect
  forceReconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket.connect()
    }
  }
}

const socketService = new SocketService()
export default socketService
